/**
 * «Повторить с этими параметрами»: чистое отображение снимка генерации (`FitGen`) в состояние формы `/generate`.
 * Эффект с `filled`-замком и очисткой `from` в адресе остаётся в `FittingPage`. `isRouter` приходит параметром
 * (`isRouterModel` живёт в `lib/fitting`, а он тянет `@/…`).
 */
import type { FitGen, FittingOptions, GenParams } from "../../../lib/fitting";
import type { Pic } from "../../../lib/uploads";

/** Снимок не годится для повтора (нет генерации/параметров/опций, либо это кадр проекта). */
export const notRepeatable = (g: FitGen | undefined, opts: FittingOptions | undefined): boolean =>
  !g || !g.params || g.kind === "shot" || !opts;

export const notFoundNote = (fromId: number): string => `Генерация #${fromId} не найдена — параметры по умолчанию.`;

/**
 * Параметры запуска из снимка. Модель повтора: у старых снимков ключа нет — gpt-image-2; нет её в селекторе
 * или она недоступна (`lost`) — параметры gpt-image-2 (качество OpenRouter у него — 400, поэтому сбрасывается).
 */
export function genFromSnapshot(
  g: FitGen,
  opts: FittingOptions,
  isRouter: (code: string | null | undefined) => boolean,
): { gen: GenParams; lost: boolean; wanted: string } {
  const p = g.params!;
  const catalog = g.kind === "fitting" ? opts.rules.fitting : g.kind === "manual" ? opts.rules.manual : opts.rules.reference;
  const codes = catalog.map((r) => r.code);
  const kept = p.rules.filter((c) => codes.includes(c));
  const rules = kept.length === codes.length ? null : kept;
  const wanted = p.model ?? g.generation_model;
  const spec = isRouter(wanted) ? opts.models.find((m) => m.code === wanted && !m.disabled) : undefined;
  const lost = isRouter(wanted) && !spec;
  const gen: GenParams = {
    preset: p.preset && opts.presets[p.preset] ? p.preset : opts.default_preset,
    ...(spec
      ? {
          model: spec.code,
          quality: p.quality && spec.quality?.includes(p.quality) ? p.quality : null,
          size: null,
          aspect: p.size_mode === "auto" ? "auto" : p.aspect_ratio,
          resolution: p.resolution && spec.resolution?.includes(p.resolution) ? p.resolution : null,
          n: Math.min(p.n, spec.max_n),
        }
      : {
          model: null,
          quality: lost ? null : p.quality || null,
          size: p.size_mode === "auto" ? "auto" : lost ? null : p.size || null,
          aspect: null,
          resolution: null,
          n: Math.min(p.n, opts.n_max),
        }),
    fit_px: opts.fit_px.includes(p.fit_px) ? p.fit_px : opts.fit_px_default,
    rules_fitting: g.kind === "fitting" ? rules : null,
    rules_reference: g.kind === "reference" ? rules : null,
    rules_manual: g.kind === "manual" ? rules : null,
    prompt: null,
  };
  return { gen, lost, wanted };
}

/** «Мой» промпт запуска (в своё поле); у авто — null. */
export const customPromptOf = (g: FitGen): string | null => (g.params!.prompt_mode === "custom" ? g.params!.prompt_custom : null);

/** Снятые галочки папки: все её фото, которых не было в запуске. */
export const uncheckedFrom = (folderPhotoIds: number[], keep: number[]): number[] =>
  folderPhotoIds.filter((id) => !keep.includes(id));

/** Вставленные картинки запуска — вместе с ролью: «Повторить» обязано вернуть и портрет внешности. */
export const extrasOf = (g: FitGen): Pic[] =>
  g.extras.map((x) => {
    const pic: Pic = { asset_id: x.asset_id, path: x.path, comment: x.comment, on: true };
    // роль пишем только когда она не умолчание: восстановленная картинка выглядит как свежая вставленная
    return x.role === "portrait" ? { ...pic, role: x.role } : pic;
  });

/** Загрузки платья запуска; пути — во входах строки по `asset_id`, без пути картинка пропадает. */
export function uploadsOf(g: FitGen): Pic[] {
  const pathOf = (assetId: number) => g.inputs.find((i) => i.asset_id === assetId)?.path;
  return g.uploads.flatMap((u) => {
    const path = pathOf(u.asset_id);
    return path ? [{ asset_id: u.asset_id, path, comment: u.comment, on: true }] : [];
  });
}

/** Пояснение под заголовком: модель недоступна / папка платья удалена; нет причин — null. */
export function fromNoteOf(
  g: Pick<FitGen, "own_dress_id" | "dress">,
  fromId: number,
  lostLabel: string | null,
): string | null {
  return (
    [
      lostLabel ? `Модель ${lostLabel} недоступна — параметры для gpt-image-2.` : null,
      g.own_dress_id === null && !g.dress?.uploaded ? `Папка платья из генерации #${fromId} удалена — добавьте платье.` : null,
    ]
      .filter(Boolean)
      .join(" ") || null
  );
}
